// Query-string builders for the v1 Risk Areas endpoints. Three wire formats:
//   • Paginated lists  → ?page&pageSize&quarters + repeated ?filter=Field:op:value
//   • Aggregates       → plain typed query params (quarters + repeated filters)
//   • Typeahead        → search + limit + typed scope params
import {
  CoderGroupTypeaheadParams,
  HeaderParams,
  KpiParams,
  ListParams,
  ProviderGroupTypeaheadParams,
  RiskAreaFilters,
} from "../types";

const enc = encodeURIComponent;

// Which group field a list endpoint filters on. /billing and /providers take
// ProviderGroup (from providerGroupIds); /coders takes CoderGroup (from
// coderIds) — two separate id spaces. Sending the wrong field for an endpoint
// silently matches nothing.
export type GroupFilterField = "ProviderGroup" | "CoderGroup";

// Builds the repeated `filter=Field:op:value` clauses the list endpoints expect.
// Multi-select values are comma-joined for the `in` operator
// (e.g. filter=WeightBucket:in:critical,high). The group clause names whichever
// field the target endpoint understands; the other id list is dropped.
const buildFilterClauses = (
  params: RiskAreaFilters,
  groupField: GroupFilterField
): string[] => {
  const clauses: string[] = [];

  if (params.categories && params.categories.length) {
    clauses.push(`filter=Category:in:${params.categories.map(enc).join(",")}`);
  }
  if (params.weightBuckets && params.weightBuckets.length) {
    clauses.push(`filter=WeightBucket:in:${params.weightBuckets.map(enc).join(",")}`);
  }
  const groupIds =
    groupField === "CoderGroup" ? params.coderIds : params.providerGroupIds;
  if (groupIds && groupIds.length) {
    clauses.push(
      `filter=${groupField}:in:${groupIds.map((id) => enc(String(id))).join(",")}`
    );
  }
  if (params.providerIds && params.providerIds.length) {
    clauses.push(
      `filter=Provider:in:${params.providerIds.map((id) => enc(String(id))).join(",")}`
    );
  }
  if (params.direction) {
    clauses.push(`filter=Direction:eq:${enc(params.direction)}`);
  }
  if (params.status && params.status.length) {
    clauses.push(`filter=Status:in:${params.status.map(enc).join(",")}`);
  }
  // Each value encoded individually so separating commas stay literal —
  // "Not Applicable" must arrive as Not%20Applicable, not with the space
  // swallowed. Comma-joined here, unlike the header endpoints' repeated param.
  if (params.outcome && params.outcome.length) {
    clauses.push(`filter=Outcome:in:${params.outcome.map(enc).join(",")}`);
  }
  return clauses;
};

// /risk-areas/billing | /provider-coder/providers | /provider-coder/coders
// groupField says which group filter the target endpoint understands — pass
// "CoderGroup" for /coders, leave it defaulted for /billing and /providers.
export const buildListQuery = (
  params: ListParams,
  groupField: GroupFilterField = "ProviderGroup"
): string => {
  const parts = [
    `page=${params.page}`,
    `pageSize=${params.pageSize}`,
    `quarters=${params.quarter}`,
    // Explicit, never omitted — see ListParams.includeFacets.
    `includeFacets=${params.includeFacets ? "true" : "false"}`,
    ...buildFilterClauses(params, groupField),
  ];
  return parts.join("&");
};

// /risk-areas/billing/groups | /provider-coder/groups
// Filter-aware: mirrors the list filters so counts match the grid, but sent as
// plain typed query params (multi-select = repeated param).
export const buildHeaderQuery = (params: HeaderParams): string => {
  const parts = [`quarters=${params.quarter}`];

  (params.categories ?? []).forEach((c) => parts.push(`categories=${enc(c)}`));
  (params.weightBuckets ?? []).forEach((b) => parts.push(`weightBuckets=${enc(b)}`));
  (params.providerGroupIds ?? []).forEach((id) => parts.push(`providerGroupIds=${enc(String(id))}`));
  (params.coderIds ?? []).forEach((id) => parts.push(`coderIds=${enc(String(id))}`));
  (params.outcome ?? []).forEach((o) => parts.push(`outcome=${enc(o)}`));
  if (params.direction) parts.push(`direction=${enc(params.direction)}`);
  (params.status ?? []).forEach((s) => parts.push(`status=${enc(s)}`));
  // Both endpoints accept groupBy over different allowed sets — see HeaderParams.
  if (params.groupBy) parts.push(`groupBy=${enc(params.groupBy)}`);

  return parts.join("&");
};

// /risk-areas/billing/kpis | /provider-coder/kpis  (quarter only)
export const buildKpiQuery = (params: KpiParams): string => `quarters=${params.quarter}`;

// Shared trailing scope params for both typeahead endpoints. Cascading
// (ADR 0005): reuses the same filter clauses the paginated list sends, so a
// group search reflects the exact population the grid is showing. groupField
// is irrelevant here — TypeaheadBaseParams carries no providerGroupIds/coderIds,
// so buildFilterClauses never emits a group clause for these callers.
const typeaheadScope = (
  params: ProviderGroupTypeaheadParams | CoderGroupTypeaheadParams
): string[] => {
  const parts: string[] = [];
  if (params.quarter) parts.push(`quarters=${params.quarter}`);
  parts.push(...buildFilterClauses(params, "ProviderGroup"));
  return parts;
};

// /risk-areas/provider-group/options  (riskScope REQUIRED)
export const buildProviderGroupTypeaheadQuery = (
  params: ProviderGroupTypeaheadParams
): string =>
  [
    `search=${enc((params.search ?? "").trim())}`,
    `limit=${params.limit ?? 200}`,
    `riskScope=${enc(params.riskScope)}`,
    ...typeaheadScope(params),
  ].join("&");

// /risk-areas/coder-group/options  (NO riskScope)
export const buildCoderGroupTypeaheadQuery = (
  params: CoderGroupTypeaheadParams
): string =>
  [`search=${enc((params.search ?? "").trim())}`, `limit=${params.limit ?? 200}`, ...typeaheadScope(params)].join(
    "&"
  );
